<template>
  <BaseDialog
    :title="$t('common.giveFeedback')"
    :is-open="isOpen"
    type="responsive"
    @close="emit('close-dialog')"
    :align-dismiss-button-right="true"
  >
    <LoadingWrapper :state="templateLoadingState">
      <ul class="flex flex-col gap-4">
        <template v-if="mode === 'unstructured' || step === 'select-members-and-template'">
          <BaseMemberSelect
            v-model="feedbackReceiverIds"
            class="bg-surface-container-high"
            :max-selectable-members="1"
            capability="canReceiveFeedback"
            :error="errors.get('feedbackReceiverId')"
            autofocus
          />
          <DialogBreak />
          <BaseConnectedButtonGroup
            v-model="mode"
            :full-width="true"
            color="outlined"
            size="M"
            :items="[
              {
                icon: 'description',
                label: $t('common.freetext'),
                value: 'unstructured',
              },
              {
                icon: 'list',
                label: $t('common.templates'),
                value: 'structured',
              },
            ]"
          />
        </template>
        <div v-if="mode === 'unstructured'">
          <WysiwygTextField
            v-model="unstructuredFeedbackFormData.text"
            :auto-focus-enabled="false"
            :placeholder="$t('giveFeedback.placeholder')"
            :error="errors.get('text')"
          />
        </div>
        <template v-if="mode === 'structured'">
          <div v-if="step === 'select-members-and-template'" class="flex flex-col gap-4">
            <FeedbackTopicList
              v-if="filteredTemplates"
              v-model="form.templateIds"
              :templates="filteredTemplates"
              :error="errors.get('templateIds')"
            />
          </div>
          <div v-if="step === 'select-question-groups'">
            <QuestionGroupSectionSelect
              v-model="form.questionGroupIds"
              :templates="configurableTemplates"
              :error="errors.get('questionGroupIds')"
            />
          </div>
          <div v-if="step === 'select-perspectives'" class="flex flex-col gap-2">
            <h3
              class="text-title-small mb-2"
              :class="[
                !errors.get('perspectiveKey') && 'text-on-surface-variant',
                errors.get('perspectiveKey') && 'text-error',
              ]"
            >
              {{ $t('giveFeedback.selectPerspectives') }}
            </h3>
            <BaseConnectedButtonGroup
              v-model="form.perspectiveKey"
              :full-width="true"
              color="outlined"
              size="M"
              :items="
                availablePerspectives.map((p) => ({
                  icon: p.icon,
                  label: p.title,
                  value: p.key,
                }))
              "
            />
            <p v-if="errors.get('perspectiveKey')" class="text-error text-body-medium mt-2 px-4">
              {{ errors.get('perspectiveKey') }}
            </p>
          </div>
          <div v-if="step === 'add-metadata'">
            <BaseInputField
              v-model="form.title"
              type="text"
              :label="$t('common.title')"
              :error="errors.get('title')"
              class="bg-surface-container-high"
            />
            <DialogBreak />
            <h3 class="text-title-medium mb-2">
              {{ $t('common.message') }} ({{ $t('common.optional') }})
            </h3>
            <WysiwygTextField v-model="form.message" />
          </div>
        </template>
      </ul>
    </LoadingWrapper>

    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton
          v-if="mode === 'unstructured'"
          color="filled"
          size="M"
          @click="sendUnstructuredFeedback"
          >{{ $t('common.sendFeedback') }}</BaseTextButton
        >
        <template v-if="mode === 'structured'">
          <BaseTextButton v-if="!isFirstStep" color="outlined" size="M" @click="previousStep">{{
            $t('common.back')
          }}</BaseTextButton>
          <BaseTextButton v-if="!isLastStep" color="filled" size="M" @click="nextStep">{{
            $t('common.continue')
          }}</BaseTextButton>
          <BaseTextButton
            v-if="isLastStep"
            color="filled"
            size="M"
            @click="sendStructuredFeedback"
            >{{ $t('common.start') }}</BaseTextButton
          >
        </template>
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton color="textOnSurface" size="M" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import { useStartFeedback } from './use-start-give-feedback'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import { watch } from 'vue'
  import BaseMemberSelect from '@/core/components/base/member-select/BaseMemberSelect.vue'
  import BaseConnectedButtonGroup from '@/core/components/base/button/BaseConnectedButtonGroup.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import QuestionGroupSectionSelect from '@/core/components/question-group-section-select/QuestionGroupSectionSelect.vue'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
  }>()

  const {
    mode,
    feedbackReceiverIds,
    form,
    filteredTemplates,
    configurableTemplates,
    availablePerspectives,
    templateLoadingState,
    errors,
    unstructuredFeedbackFormData,
    sendUnstructuredFeedback,
    sendStructuredFeedback,
    step,
    nextStep,
    previousStep,
    isFirstStep,
    isLastStep,
    clearForm,
  } = useStartFeedback(() => emit('close-dialog'))

  watch(
    () => props.isOpen,
    (newValue) => {
      if (!newValue) {
        clearForm()
      }
    },
  )
</script>
